"use client"

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined"
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded"
import { useGetIdentity, useLogout } from "@refinedev/core"
import { ThemedSider, useThemedLayoutContext } from "@refinedev/mui"
import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material"
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined"
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined"
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined"
import { AppTitle } from "@/components/layout/app-title"
import { APP_EDGE_DOCK_HEIGHT } from "@/components/layout/layout-constants"

type Identity = {
  name?: string
  email?: string
  role?: string
}

function UserDock({ collapsed }: { collapsed: boolean }) {
  const { data: user } = useGetIdentity<Identity>()
  const { mutate: logout } = useLogout()
  const router = useRouter()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const canManageSystem = user?.role === "BOSS"

  const menuItems = useMemo(() => {
    const base = [
      {
        key: "profile",
        label: "个人设置",
        icon: <AccountCircleOutlinedIcon fontSize="small" />,
        onClick: () => router.push("/dashboard/profile"),
      },
    ]

    if (canManageSystem) {
      base.push(
        {
          key: "users",
          label: "用户管理",
          icon: <ManageAccountsOutlinedIcon fontSize="small" />,
          onClick: () => router.push("/dashboard/users"),
        },
        {
          key: "settings",
          label: "系统参数",
          icon: <TuneOutlinedIcon fontSize="small" />,
          onClick: () => router.push("/dashboard/settings"),
        },
        {
          key: "org",
          label: "组织与权限",
          icon: <SettingsSuggestOutlinedIcon fontSize="small" />,
          onClick: () => router.push("/dashboard/org"),
        },
      )
    }

    return base
  }, [canManageSystem, router])

  const badge = (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
        border: "1px solid rgba(37,99,235,0.16)",
        color: "#2563eb",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        position: "relative",
        zIndex: 1,
      }}
    >
      PO
    </Box>
  )

  if (collapsed) {
    return (
      <>
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ minHeight: APP_EDGE_DOCK_HEIGHT, px: 1, py: 1.25, cursor: "pointer" }}
        >
          <Box sx={{ position: "relative", display: "grid", placeItems: "center" }}>
            <Box
              sx={{
                position: "absolute",
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "rgba(37,99,235,0.10)",
                animation: "appAvatarGlow 5s ease-in-out infinite",
              }}
            />
            {badge}
          </Box>
        </Stack>
        <UserDockMenu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          items={menuItems}
          onLogout={() => logout()}
        />
      </>
    )
  }

  return (
    <>
      <Box
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          minHeight: APP_EDGE_DOCK_HEIGHT,
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 1.25,
          cursor: "pointer",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: "100%" }}>
          <Box sx={{ position: "relative", display: "grid", placeItems: "center" }}>
            <Box
              sx={{
                position: "absolute",
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "rgba(37,99,235,0.10)",
                animation: "appAvatarGlow 5s ease-in-out infinite",
              }}
            />
            <Box sx={{ animation: "appAvatarFloat 6s ease-in-out infinite" }}>{badge}</Box>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {user?.email && (
              <Typography sx={{ fontSize: 11, color: "rgba(15,23,42,0.5)" }} noWrap>
                {user.email}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
      <UserDockMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        items={menuItems}
        onLogout={() => logout()}
      />
    </>
  )
}

function UserDockMenu({
  anchorEl,
  open,
  onClose,
  items,
  onLogout,
}: {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  items: Array<{ key: string; label: string; icon: ReactNode; onClick: () => void }>
  onLogout: () => void
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      autoFocus={false}
      disableAutoFocusItem
      MenuListProps={{ dense: true, autoFocusItem: false }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 224,
          },
        },
      }}
    >
      {items.map((item) => (
        <MenuItem
          key={item.key}
          onClick={() => {
            onClose()
            item.onClick()
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        onClick={() => {
          onClose()
          onLogout()
        }}
      >
        <ListItemIcon>
          <LogoutRoundedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="退出登录" />
      </MenuItem>
    </Menu>
  )
}

export function AppSider() {
  const { siderCollapsed } = useThemedLayoutContext()

  return (
    <ThemedSider
      Title={AppTitle}
      render={({ items, collapsed }) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <MenuGroups collapsed={collapsed} items={items} />

          <Box
            sx={{
              px: collapsed ? 0.75 : 0,
              pb: 0,
              mt: "auto",
              minHeight: APP_EDGE_DOCK_HEIGHT,
              position: "sticky",
              bottom: 0,
              borderTop: "1px solid rgba(15,23,42,0.08)",
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(10px)",
            }}
          >
            <UserDock collapsed={siderCollapsed} />
          </Box>
        </Box>
      )}
    />
  )
}

function GroupLabel({ collapsed, label }: { collapsed: boolean; label: string }) {
  if (collapsed) return <Divider sx={{ mx: 0.75, mb: 1.25 }} />
  return (
    <Typography
      sx={{
        px: 1.5,
        pb: 0.9,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.8,
        color: "rgba(15,23,42,0.34)",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
  )
}

function MenuGroups({ collapsed, items }: { collapsed: boolean; items: ReactNode }) {
  const allItems = Array.isArray(items) ? items : [items]

  // 经营总览(1) + 运营中心(3:账号数据+询单中心+计调资源) + 业务主线(2:方案+订单) + 资源中心(2) + 结算与管控(2) + 支持与工具(3)
  // 2026-06-04 起:运营中心移到业务主线之前 —— "先有账号运营,才会有业务主线"
  // 2026-06-15 起:询单中心并入运营中心 —— 询单受理是运营动作,跟账号数据同组
  // appResources 顺序:dashboard / ops-accounts / inquiries / dispatch-resources / quotations / orders / customers / suppliers / finance / approvals / toolbox...
  // 详见 docs/design/07-sidebar-nav-order.md
  const overviewItems = allItems.slice(0, 1)
  const operationsItems = allItems.slice(1, 4)
  const mainChainItems = allItems.slice(4, 6)
  const resourceItems = allItems.slice(6, 8)
  const backendItems = allItems.slice(8, 10)
  const supportItems = allItems.slice(10)

  const groups = [
    { label: "", items: overviewItems },
    { label: "运营中心", items: operationsItems },
    { label: "业务主线", items: mainChainItems },
    { label: "资源中心", items: resourceItems },
    { label: "结算与管控", items: backendItems },
    { label: "支持与工具", items: supportItems },
  ]

  return (
    <Box
      sx={{
        flex: 1,
        px: collapsed ? 0.75 : 1.25,
        py: 1.25,
        "& .MuiListItemButton-root": {
          px: collapsed ? 1 : 1.5,
        },
        "& .MuiListItemIcon-root": {
          minWidth: 24,
        },
        "& .MuiListItemText-primary": {
          letterSpacing: -0.1,
        },
      }}
    >
      {groups.map((group) =>
        group.items.length > 0 ? (
          <Box key={group.label || "overview"} sx={{ pt: group.label ? (collapsed ? 0.75 : 1.25) : 0 }}>
            {group.label && <GroupLabel collapsed={collapsed} label={group.label} />}
            {group.items}
          </Box>
        ) : null,
      )}
    </Box>
  )
}
